import { useState } from "react";
import { Mail, Send } from "lucide-react";
import { SiDiscord } from "react-icons/si";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitContact, subscribeNewsletter } from "@/lib/api";
import { BRAND } from "@/lib/brand";

export default function ContactSection() {
  const [contact, setContact] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const onContactSubmit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.subject || !contact.message) {
      toast.error("Please fill all fields.");
      return;
    }
    setContactSubmitting(true);
    try {
      await submitContact(contact);
      toast.success("Message received. Legends will be in touch.");
      setContact({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message. Try again.");
    } finally {
      setContactSubmitting(false);
    }
  };

  const onNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Email required.");
      return;
    }
    setNewsletterSubmitting(true);
    try {
      await subscribeNewsletter(newsletterEmail);
      toast.success("You're on the dispatch list.");
      setNewsletterEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">
            ⌁ Signal The House
          </p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black uppercase tracking-tight text-white">
            Speak To <span className="gold-text">The Court</span>
          </h2>
          <p className="mt-5 text-zinc-400">
            Sponsorships, partnerships, press, tryouts, or fan mail — every
            scroll passes the throne. We respond within 48 hours.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${BRAND.contactEmail}`}
              data-testid="contact-email-link"
              className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37] transition-colors"
            >
              <Mail size={18} className="text-[#D4AF37]" />
              <span>{BRAND.contactEmail}</span>
            </a>
            <a
              href={BRAND.discordUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-discord-link"
              className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37] transition-colors"
            >
              <SiDiscord size={18} className="text-[#D4AF37]" />
              <span>Join the TGL Discord</span>
            </a>
          </div>

          <div
            className="mt-10 border border-[#D4AF37]/30 cut-corners p-6 bg-black/40"
            data-testid="newsletter-block"
          >
            <h3 className="font-display text-lg font-bold uppercase text-white">
              The TGL Dispatch
            </h3>
            <p className="mt-1 text-xs text-zinc-400">
              Bi-weekly briefing. Tournament results, free tickets, and roster
              moves.
            </p>
            <form
              onSubmit={onNewsletterSubmit}
              className="mt-4 flex gap-2"
              data-testid="newsletter-form"
            >
              <Input
                type="email"
                required
                placeholder="your@email.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
              <Button
                type="submit"
                disabled={newsletterSubmitting}
                data-testid="newsletter-submit-button"
                className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners shrink-0"
              >
                {newsletterSubmitting ? "..." : "Join"}
              </Button>
            </form>
          </div>
        </div>

        <form
          onSubmit={onContactSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 border border-white/10 cut-corners bg-[#0A0A0A] p-6 lg:p-10 grid gap-5"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Your Name
              </Label>
              <Input
                required
                data-testid="contact-name-input"
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Email
              </Label>
              <Input
                required
                type="email"
                data-testid="contact-email-input"
                value={contact.email}
                onChange={(e) =>
                  setContact({ ...contact, email: e.target.value })
                }
                className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Subject
            </Label>
            <Input
              required
              data-testid="contact-subject-input"
              value={contact.subject}
              onChange={(e) =>
                setContact({ ...contact, subject: e.target.value })
              }
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Message
            </Label>
            <Textarea
              required
              rows={6}
              data-testid="contact-message-input"
              value={contact.message}
              onChange={(e) =>
                setContact({ ...contact, message: e.target.value })
              }
              className="mt-1 bg-black border-white/15 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] min-h-32"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={contactSubmitting}
              data-testid="contact-submit-button"
              className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners gap-2"
            >
              {contactSubmitting ? "Sending..." : "Send Signal"}
              <Send size={14} />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
